import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import {
  OrganisationTagExternal,
  useOrganisationTagsExternalQuery,
  useOrganisationTagUpdateSubscriptionStatusMutation,
} from '../../../services';
import { ORG_TAGS_SIZE_LIMIT } from '../../../shared';

interface NotificationTagChange {
  tagId: number;
  isEnabled: boolean;
}

export const useTagNotificationPreferences = () => {
  const { t } = useTranslation();

  const { data, isLoading } = useOrganisationTagsExternalQuery({ size: ORG_TAGS_SIZE_LIMIT });

  const [updateTagStatus] = useOrganisationTagUpdateSubscriptionStatusMutation();

  const [tagOverrideById, setTagOverrideById] = useState<Record<number, boolean>>({});

  const isTagEnabled = useCallback(
    ({ id, subscriptionStatus }: OrganisationTagExternal) =>
      tagOverrideById[id] ?? subscriptionStatus,
    [tagOverrideById]
  );

  const setTagEnabled = useCallback(
    async ({ tagId, isEnabled }: NotificationTagChange) => {
      setTagOverrideById((tagOverrideByIdPrev) => ({ ...tagOverrideByIdPrev, [tagId]: isEnabled }));

      const revertTag = () => {
        setTagOverrideById((tagOverrideByIdPrev) => ({
          ...tagOverrideByIdPrev,
          [tagId]: !isEnabled,
        }));

        toast.error(t('common.error-message'));
      };

      try {
        const isUpdated = await updateTagStatus({ tagId, status: isEnabled }).unwrap();

        if (!isUpdated) {
          revertTag();
        }
      } catch (_) {
        revertTag();
      }
    },
    [t, updateTagStatus]
  );

  return useMemo(
    () => ({ tags: data?.items ?? [], isLoadingTags: isLoading, isTagEnabled, setTagEnabled }),
    [data?.items, isLoading, isTagEnabled, setTagEnabled]
  );
};
